import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Training } from '../types';
import { Card } from './ui/Card';
import { Clock } from 'lucide-react';
import { formatDate } from '../utils/dateFormat';
import './TrainingDetails.css';

interface TrainingDetailsProps {
  training: Training;
}

export const TrainingDetails: React.FC<TrainingDetailsProps> = ({ training }) => {
  const { t } = useTranslation();

  return (
    <div className="training-details">
      <h1 className="training-details-title">{training.title}</h1>

      <Card className="training-info">
        <div className="info-row">
          <span className="info-label">{t('date')}:</span>
          <span className="info-value">{formatDate(training.date, true)}</span>
        </div>
        {training.user && (
          <div className="info-row">
            <span className="info-label">{t('student')}:</span>
            <span className="info-value">{training.user.name}</span>
          </div>
        )}
        {training.observations && (
          <div className="info-row">
            <span className="info-label">{t('observations')}:</span>
            <span className="info-value">{training.observations}</span>
          </div>
        )}
      </Card>

      {/* Warmup Section */}
      {training.warmup_section && training.warmup_section.exercises.length > 0 && (
        <Card className="section-block warmup-block">
          <div className="section-block-header">
            <h2 className="section-block-title">{t('warmup')}</h2>
            <span className="section-count">{training.warmup_section.exercises.length} {t('exercises').toLowerCase()}</span>
          </div>

          {/* Global parameters for warmup section */}
          <div className="main-params">
            {training.warmup_section.params.seconds !== undefined && training.warmup_section.params.seconds > 0 && (
              <div className="param-item">
                <span className="param-label">{t('seconds')}:</span>
                <span className="param-value">{training.warmup_section.params.seconds}s</span>
              </div>
            )}
            {training.warmup_section.params.rest_between_exercises !== undefined && training.warmup_section.params.rest_between_exercises > 0 && (
              <div className="param-item">
                <span className="param-label">Descanso:</span>
                <span className="param-value">
                  <Clock size={16} /> {training.warmup_section.params.rest_between_exercises}s
                </span>
              </div>
            )}
          </div>

          <div className="exercises-compact-list">
            {training.warmup_section.exercises.map((exercise, index) => (
              <div key={exercise.id} className="exercise-compact-item">
                <span className="exercise-compact-number">{index + 1}</span>
                <div className="exercise-compact-content">
                  <div className="exercise-compact-header">
                    <span className="exercise-compact-name">{exercise.name}</span>
                  </div>
                  {exercise.description && (
                    <p className="exercise-compact-description">{exercise.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Main Section */}
      {training.main_section && training.main_section.exercises.length > 0 && (
        <Card className="section-block main-block">
          <div className="section-block-header">
            <h2 className="section-block-title">{t('mainPart')}</h2>
            <span className="section-count">{training.main_section.exercises.length} {t('exercises').toLowerCase()}</span>
          </div>

          {/* Global parameters for main section */}
          <div className="main-params">
            <div className="param-item">
              <span className="param-label">{t('series')}:</span>
              <span className="param-value">{training.main_section.params.series || 0}</span>
            </div>
            <div className="param-item">
              <span className="param-label">{t('seconds')}:</span>
              <span className="param-value">{training.main_section.params.seconds || 0}s</span>
            </div>
            <div className="param-item">
              <span className="param-label">{t('restBetweenSeries')}:</span>
              <span className="param-value">
                <Clock size={16} /> {training.main_section.params.rest_between_series || 0}s
              </span>
            </div>
            <div className="param-item">
              <span className="param-label">{t('restBetweenExercises')}:</span>
              <span className="param-value">
                <Clock size={16} /> {training.main_section.params.rest_between_exercises || 0}s
              </span>
            </div>
          </div>

          <div className="exercises-compact-list">
            {training.main_section.exercises.map((exercise, index) => (
              <div key={exercise.id} className="exercise-compact-item">
                <span className="exercise-compact-number">{index + 1}</span>
                <div className="exercise-compact-content">
                  <div className="exercise-compact-header">
                    <span className="exercise-compact-name">{exercise.name}</span>
                  </div>
                  {exercise.description && (
                    <p className="exercise-compact-description">{exercise.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Cooldown Section */}
      {training.cooldown_section && training.cooldown_section.exercises.length > 0 && (
        <Card className="section-block cooldown-block">
          <div className="section-block-header">
            <h2 className="section-block-title">{t('cooldown')}</h2>
            <span className="section-count">{training.cooldown_section.exercises.length} {t('exercises').toLowerCase()}</span>
          </div>

          {/* Global parameters for cooldown section */}
          <div className="main-params">
            {training.cooldown_section.params.seconds !== undefined && training.cooldown_section.params.seconds > 0 && (
              <div className="param-item">
                <span className="param-label">{t('seconds')}:</span>
                <span className="param-value">{training.cooldown_section.params.seconds}s</span>
              </div>
            )}
            {training.cooldown_section.params.rest_between_exercises !== undefined && training.cooldown_section.params.rest_between_exercises > 0 && (
              <div className="param-item">
                <span className="param-label">Descanso:</span>
                <span className="param-value">
                  <Clock size={16} /> {training.cooldown_section.params.rest_between_exercises}s
                </span>
              </div>
            )}
          </div>

          <div className="exercises-compact-list">
            {training.cooldown_section.exercises.map((exercise, index) => (
              <div key={exercise.id} className="exercise-compact-item">
                <span className="exercise-compact-number">{index + 1}</span>
                <div className="exercise-compact-content">
                  <div className="exercise-compact-header">
                    <span className="exercise-compact-name">{exercise.name}</span>
                  </div>
                  {exercise.description && (
                    <p className="exercise-compact-description">{exercise.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};
